import { formatConfirmationDate } from '../../../features/custom-fields/utils/confirmation-date.util';
import { formatItemListSummary } from '../../../features/custom-fields/utils/item-list.util';
import { CfItemListFieldComponent } from '../../../features/custom-fields/components/cf-item-list-field/cf-item-list-field.component';
import { createEmptyCustomField } from '../../../features/custom-fields/utils/custom-field-editor.util';
import { Component, computed, input, output, signal, HostListener, inject } from '@angular/core';
import { CompanyUsersService } from '../../../core';
import { isGuidString } from '../../../core/utils/guid.util';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { AppComboboxComponent, type ComboboxOptionGroup } from '../combobox/app-combobox.component';
import { DatePickerComponent } from '../date-picker/date-picker.component';
import type { SelectItem } from '../select/select.component';
import { BoardStageMetricPickerComponent } from '../../../features/workflows/components/board-stage-metric-picker/board-stage-metric-picker.component';
import type { BoardStageMetricFieldOption } from '../../../features/workflows/components/board-stage-metric-picker/board-stage-metric-picker.component';
import { BoardStageMetricsComponent } from '../../../features/workflows/components/board-stage-metrics/board-stage-metrics.component';
import type {
  BoardMetricDisplayChip,
  BoardStageMetricConfig,
} from '../../../features/workflows/models/board-stage-metric.model';
import { TPipe } from '../../pipes/t.pipe';
import {
  formatCustomDateTimeForDisplay,
  formatCustomTimeForDisplay,
  formatFileFieldValueForList,
  looksLikeFileFieldJsonString,
} from '../../../shared/utils';
import {
  formatStoredBrazilPhoneForDisplay,
  normalizeBrazilPhoneForSubmit,
} from '../../../shared/utils/brazil-phone.util';
import type { ItemPriority } from '../../../features/workflows/models/workflow-item.model';
import { CfRatingFieldComponent } from '../../../features/custom-fields/components/cf-rating-field/cf-rating-field.component';
import {
  clampNumberFieldDecimalPlaces,
  effectiveListNumberDecimalPlaces,
  formatNumberFieldForDisplayPtBr,
  formatNumberFieldValueForApi,
  parseFlexibleNumberString,
  parseMoneyStringPtBr,
  roundToDecimalPlaces,
  shouldPreserveLeadingZeroIntegerRaw,
} from '../../../features/custom-fields/utils/number-field.util';
import { ListFileFieldCellComponent } from '../list-file-field-cell/list-file-field-cell.component';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import type {
  CustomFieldRawValue,
  CustomFieldViewModel,
} from '../../../features/custom-fields/models/custom-field.model';
import { parseRatingQuickViewDisplay } from '../../../features/custom-fields/utils/rating-field.util';
import { CfInlineCopyButtonComponent } from '../../../features/custom-fields/components/cf-inline-copy-button/cf-inline-copy-button.component';

const COL_MIN_WIDTH = 60;
const COL_MAX_WIDTH = 600;
const NAME_COLUMN_MIN_WIDTH = 60;

function hexRgb(hex: string): { r: number; g: number; b: number } | null {
  const n = hex.trim().replace(/^#/, '');
  if (n.length === 6 && /^[0-9a-fA-F]{6}$/.test(n)) {
    return {
      r: Number.parseInt(n.slice(0, 2), 16),
      g: Number.parseInt(n.slice(2, 4), 16),
      b: Number.parseInt(n.slice(4, 6), 16),
    };
  }
  return null;
}

function rgbaFromHex(hex: string, alpha: number): string | null {
  const rgb = hexRgb(hex);
  if (!rgb) return null;
  return `rgb(${rgb.r} ${rgb.g} ${rgb.b} / ${alpha})`;
}

interface ResizeState {
  columnId: string;
  col: ListViewColumn;
  pointerId: number;
  /** Última posição X do ponteiro (incremental: evita “piscar” ao encolher com scroll/layout). */
  lastClientX: number;
  scrollParent: HTMLElement | null;
}

/** Coluna configurável da List View */
export interface ListViewColumn {
  id: string;
  label: string;
  type: 'name' | 'code' | 'description' | 'latestComment' | 'status' | 'assignee' | 'priority' | 'dueDate' | 'custom';
  customFieldKey?: string;
  minWidth?: number;
  width?: number;
}

export interface GroupedListCustomFieldEditor {
  kind: 'text' | 'select' | 'rating' | 'file' | 'phone' | 'number' | 'date' | 'datetime' | 'confirmation-date' | 'item-list' | 'time' | 'boolean';
  /** Campos calculados (ex.: FORMULA) não aceitam edição inline. */
  readOnly?: boolean;
  required?: boolean;
  /** Exibir célula como avatar (campos USER/PERSON). */
  displayAsPerson?: boolean;
  options?: string[];
  /** Incluir `userId` quando o valor for GUID ou nome resolvível (avatar no combobox). */
  selectOptions?: SelectItem[];
  emptyLabel?: string;
  ratingLevels?: number;
  /** Campos NUMBER na lista rápida. */
  numberDecimalPlaces?: number;
  numberShowPercentSuffix?: boolean;
  /** Campo CURRENCY: formatação BRL (pt-BR) na célula; `kind` continua `number`. */
  formatCurrency?: boolean;
  /** Quando true, exibe ícone de copiar ao lado do valor (hover only na lista/kanban). */
  copyable?: boolean;
}

export interface GroupedListItem {
  id: string;
  /** User-selected color applied only to table rows. */
  rowColorHex?: string | null;
  code?: string | null;
  title: string;
  /** Descrição nativa do card (não confundir com campo personalizado "Descrição"). */
  description?: string | null;
  latestComment?: string | null;
  status: string;
  assigneeName: string;
  assigneeUserId?: string | null;
  priority: string;
  dueDate: string | null;
  customFields?: Record<string, string | null>;
  quickFields?: GroupedListQuickField[];
}

export interface GroupedListQuickField {
  label: string;
  value: string;
  fieldType: number;
  colorHex: string | null;
  isDefault: boolean;
}

export interface GroupedByStatus {
  status: string;
  label: string;
  color?: string;
  items: GroupedListItem[];
  depth?: number;
  ancestorKeys?: string[];
  hasChildren?: boolean;
  fieldLabel?: string;
}

/** Métricas por etapa na list view (alinhado ao board / stageMetricConfigMap). */
export interface GroupedListStageMetricsRow {
  storedConfig: BoardStageMetricConfig;
  chips: BoardMetricDisplayChip[];
  /** Lista: mostrar "(N)" no título só quando não há chips de métrica numérica configurados. */
  showListGroupParenCount: boolean;
}

const DEFAULT_MIN_WIDTH: Record<string, number> = {
  name: 220,
  code: 88,
  description: 160,
  latestComment: 260,
  status: 120,
  assignee: 120,
  priority: 120,
  dueDate: 120,
  custom: 120,
};

@Component({
  selector: 'app-grouped-list',
  standalone: true,
  imports: [
    LucideAngularModule,
    BoardStageMetricPickerComponent,
    BoardStageMetricsComponent,
    TPipe,
    AppComboboxComponent,
    DatePickerComponent,
    CfRatingFieldComponent,
    CfItemListFieldComponent,
    ListFileFieldCellComponent,
    UserAvatarComponent,
    CfInlineCopyButtonComponent,
  ],
  templateUrl: './grouped-list.component.html',
  styles: [
    `
      .gl-group-shell {
        border-radius: 12px;
        border: 1px solid #e4d7c4;
        box-shadow:
          0 1px 1px rgb(15 23 42 / 0.02),
          0 0 0 1px color-mix(in srgb, #ffffff 45%, transparent);
        background: #ffffff;
      }

      .gl-group-shell--stage {
        border-top: 1px solid color-mix(in srgb, var(--gl-stage) 48%, #d8cbb8);
      }

      .gl-group-shell--parent {
        padding-top: 10px;
        padding-bottom: 10px;
        background: var(--hover);
      }

      .gl-group-head {
        padding-bottom: 0.25rem;
      }

      .gl-group-divider {
        height: 1px;
        background: color-mix(in srgb, var(--border) 55%, transparent);
        margin-top: 0.5rem;
        margin-bottom: 0.75rem;
      }

      .gl-col-head {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: color-mix(in srgb, var(--muted) 88%, var(--text));
        border-bottom: 1px solid color-mix(in srgb, var(--border) 65%, transparent);
        padding-top: 0.25rem;
        padding-bottom: 0.5rem;
      }

      .gl-row-list {
        display: flex;
        flex-direction: column;
        gap: 3px;
        padding-top: 2px;
        padding-bottom: 4px;
      }

      .gl-row-grid {
        min-height: 34px;
        padding-top: 2px;
        padding-bottom: 2px;
        border-radius: 0;
        transition:
          background-color 0.16s ease,
          box-shadow 0.16s ease;
      }

      .gl-row-grid:hover {
        background-color: color-mix(in srgb, var(--hover) 58%, var(--bg-surface));
        box-shadow: none;
      }

      .gl-row-grid--colored {
        background-color: color-mix(in srgb, var(--gl-row-color) 16%, var(--bg-surface));
      }

      .gl-row-grid--colored:hover {
        background-color: color-mix(in srgb, var(--gl-row-color) 23%, var(--bg-surface));
      }

      .gl-cell-pad {
        padding-left: 0.375rem;
        padding-right: 0.375rem;
      }

      .gl-icon-btn {
        min-width: 32px;
        min-height: 32px;
        border-radius: 8px;
        transition:
          background-color 0.15s ease,
          color 0.15s ease,
          border-color 0.15s ease;
      }

      .gl-priority-pill {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        max-width: 100%;
        min-height: 26px;
        padding: 4px 10px;
        border-radius: 0.5rem;
        font-size: 11px;
        font-weight: 700;
        line-height: 1.2;
        border: none;
        box-sizing: border-box;
        transition: filter 0.15s ease, opacity 0.15s ease;
      }

      .gl-priority-pill--low {
        background: #64748b;
        color: #ffffff;
      }

      .gl-priority-pill--med {
        background: #ca8a04;
        color: #ffffff;
      }

      .gl-priority-pill--high {
        background: #dc2626;
        color: #ffffff;
      }

      .gl-priority-pill--default {
        background: #94a3b8;
        color: #ffffff;
      }

      :host-context(html.dark) .gl-priority-pill--low,
      :host-context(html[data-theme='dark']) .gl-priority-pill--low {
        background: #475569;
        color: #f8fafc;
      }

      :host-context(html.dark) .gl-priority-pill--med,
      :host-context(html[data-theme='dark']) .gl-priority-pill--med {
        background: #f59e0b;
        color: #111827;
      }

      :host-context(html.dark) .gl-priority-pill--high,
      :host-context(html[data-theme='dark']) .gl-priority-pill--high {
        background: #ef4444;
        color: #ffffff;
      }

      :host-context(html.dark) .gl-priority-pill--default,
      :host-context(html[data-theme='dark']) .gl-priority-pill--default {
        background: #64748b;
        color: #f8fafc;
      }

      .gl-due-btn {
        border-radius: 8px;
        font-variant-numeric: tabular-nums;
        transition:
          background-color 0.15s ease,
          color 0.15s ease;
      }

      .gl-due-btn.gl-due--soon {
        background: rgb(234 179 8 / 0.09);
        color: #a16207;
      }

      .gl-due-btn.gl-due--overdue {
        background: rgb(239 68 68 / 0.09);
        color: #b91c1c;
      }

      .gl-boolean-chip {
        min-height: 24px;
        width: 100%;
        border: 1px solid color-mix(in srgb, var(--border) 70%, transparent);
        border-radius: 999px;
        padding: 2px 8px 2px 4px;
        background: color-mix(in srgb, var(--bg-surface) 86%, var(--hover));
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
      }

      .gl-boolean-switch {
        width: 30px;
        height: 18px;
        border-radius: 999px;
        border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
        background: color-mix(in srgb, var(--muted) 26%, transparent);
        display: inline-flex;
        align-items: center;
        padding: 1px;
        transition: background-color 0.16s ease;
      }

      .gl-boolean-switch::after {
        content: '';
        width: 14px;
        height: 14px;
        border-radius: 999px;
        background: #ffffff;
        box-shadow: 0 1px 2px rgb(0 0 0 / 0.18);
        transform: translateX(0);
        transition: transform 0.16s ease;
      }

      .gl-boolean-switch--checked {
        background: #22c55e;
        border-color: #22c55e;
      }

      .gl-boolean-switch--checked::after {
        transform: translateX(12px);
      }

      .gl-boolean-chip__text {
        min-width: 0;
        font-size: 11px;
        font-weight: 700;
        color: color-mix(in srgb, var(--muted) 88%, var(--text));
      }

      :host-context(html.dark) .gl-due-btn.gl-due--soon,
      :host-context(html[data-theme='dark']) .gl-due-btn.gl-due--soon {
        background: rgb(234 179 8 / 0.12);
        color: #fbbf24;
      }

      :host-context(html.dark) .gl-due-btn.gl-due--overdue,
      :host-context(html[data-theme='dark']) .gl-due-btn.gl-due--overdue {
        background: rgb(248 113 113 / 0.1);
        color: #fca5a5;
      }

      .group-table-scroll {
        overflow-x: auto;
        overflow-y: hidden;
        padding-bottom: 4px;
        margin-bottom: 2px;
        scrollbar-gutter: stable;
        scrollbar-width: thin;
        scrollbar-color: color-mix(in srgb, var(--border) 55%, transparent) transparent;
      }

      .group-table-scroll::-webkit-scrollbar {
        height: 7px;
      }

      .group-table-scroll::-webkit-scrollbar-track {
        background: transparent;
      }

      .group-table-scroll::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: color-mix(in srgb, var(--border) 52%, transparent);
        border: 2px solid transparent;
        background-clip: padding-box;
      }

      .group-table-content {
        min-width: max-content;
      }

      .resize-handle {
        touch-action: none;
        user-select: none;
        transition: background-color 0.15s ease, border-color 0.15s ease;
      }
    `,
  ],
  host: {
    '[class.resize-active]': 'resizing()',
    '[style.cursor]': 'resizing() ? "col-resize" : null',
    '[style.userSelect]': 'resizing() ? "none" : null',
  },
})
export class GroupedListComponent {
  readonly pendingReplyCardIds = input<ReadonlySet<string>>(new Set());
  private readonly router = inject(Router);
  private readonly companyUsers = inject(CompanyUsersService);
  private pendingNameClickTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    void this.companyUsers.ensureLoaded();
  }

  // View queries can expose this component before Angular binds the first group list.
  groups = input<GroupedByStatus[]>([]);
  readonly showGroupHeaders = input(true);
  columns = input<ListViewColumn[]>([]);
  statuses = input<Array<{ id: string; label: string }>>([]);
  selectionMode = input(false);
  selectionDisabled = input(false);
  selectedItemIds = input<ReadonlySet<string>>(new Set());
  itemSelectionToggle = output<string>();
  itemUrlFn = input.required<(item: GroupedListItem) => string[]>();
  formatDateFn = input.required<(value: string | null) => string>();
  customFieldOptionColors = input<Record<string, Record<string, string>>>({});
  /** Opções de prioridade para edição inline (coluna prioridade). */
  priorities = input<ItemPriority[]>([]);
  assignees = input<string[]>([]);
  /** Painel de responsável estilo pessoa (secções + avatares); lista plana deduplicada em `assigneeComboboxFlatOptions()`. */
  assigneeOptionGroups = input<ComboboxOptionGroup[]>([]);
  customFieldEditors = input<Record<string, GroupedListCustomFieldEditor>>({});

  /** Quando false, não navega ao abrir detalhe; emite `itemDetailNavigate`. */
  navigateToItemDetail = input(true);

  /** Quando true, cada grupo exibe picker + chips conforme `stageMetricsForGroups`. */
  enableStageMetrics = input(false);
  stageMetricsForGroups = input<Record<string, GroupedListStageMetricsRow>>({});
  stageMetricSumFieldOptions = input<BoardStageMetricFieldOption[]>([]);
  stageMetricPanelAria = input('');
  stageMetricTriggerAria = input('');

  addItemInGroup = output<string>();
  stageMetricSelectionChange = output<{ stageId: string; config: BoardStageMetricConfig }>();
  itemStatusChange = output<{ itemId: string; status: string }>();
  itemTitleCommit = output<{ itemId: string; title: string }>();
  itemAssigneeChange = output<{ itemId: string; assigneeName: string }>();
  itemPriorityChange = output<{ itemId: string; priority: ItemPriority }>();
  itemDueDateCommit = output<{ itemId: string; dueDate: string | null }>();
  itemCustomFieldCommit = output<{ itemId: string; fieldKey: string; value: string | number | boolean | null }>();
  /** Novo arquivo para campo FILE (upload no card + merge JSON). */
  itemCustomFieldFileAppend = output<{ itemId: string; fieldKey: string; file: File }>();
  columnResize = output<{ columnId: string; width: number }>();
  columnResizeEnd = output<void>();
  columnReorder = output<string[]>();
  itemDetailNavigate = output<GroupedListItem>();

  readonly groupsCollapsed = signal<Record<string, boolean>>({});
  readonly visibleGroups = computed(() => {
    const collapsed = this.groupsCollapsed();
    return this.groups().filter(group => !(group.ancestorKeys ?? []).some(key => collapsed[key]));
  });
  readonly visibleItemIds = computed(() => this.visibleGroups()
    .filter(group => !group.hasChildren && !this.isGroupCollapsed(group.status))
    .flatMap(group => group.items.map(item => item.id)));
  readonly activeEditCell = signal<{ itemId: string; columnId: string } | null>(null);

  toggleGroupCollapsed(status: string): void {
    this.groupsCollapsed.update((prev) => ({ ...prev, [status]: !prev[status] }));
  }

  isGroupCollapsed(status: string): boolean {
    return !!this.groupsCollapsed()[status];
  }

  metricsRowFor(group: GroupedByStatus): GroupedListStageMetricsRow | undefined {
    if (!this.enableStageMetrics()) return undefined;
    return this.stageMetricsForGroups()[group.status];
  }

  showGroupHeaderParenCount(group: GroupedByStatus): boolean {
    if (!this.enableStageMetrics()) return true;
    const row = this.stageMetricsForGroups()[group.status];
    if (!row) return true;
    return row.showListGroupParenCount;
  }

  minWidthFor(col: ListViewColumn): number {
    return col.type === 'name' ? NAME_COLUMN_MIN_WIDTH : COL_MIN_WIDTH;
  }

  effectiveWidth(col: ListViewColumn): number {
    const base = col.width ?? col.minWidth ?? DEFAULT_MIN_WIDTH[col.type] ?? 100;
    const minW = this.minWidthFor(col);
    return Math.max(minW, Math.min(COL_MAX_WIDTH, base));
  }

  private readonly gripWidthPx = 14;

  gridTemplateColumns(): string {
    const cols = this.columns();
    if (!cols.length) return '';
    return (this.selectionMode() ? '40px ' : '') + cols
      .map((c) => {
        const w = this.effectiveWidth(c);
        const content = `${w}px`;
        return `${content} ${this.gripWidthPx}px`;
      })
      .join(' ');
  }

  readonly resizing = signal<ResizeState | null>(null);

  startResize(col: ListViewColumn, event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const el = event.currentTarget as HTMLElement;
    try {
      el.setPointerCapture(event.pointerId);
    } catch {
      // ignorar (ex.: pointerId inválido)
    }
    const scrollParent = el.closest('.group-table-scroll') as HTMLElement | null;
    this.resizing.set({
      columnId: col.id,
      col,
      pointerId: event.pointerId,
      lastClientX: event.clientX,
      scrollParent,
    });
  }

  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMove(event: PointerEvent): void {
    const state = this.resizing();
    if (!state || event.pointerId !== state.pointerId) return;
    const liveCol = this.columns().find((c) => c.id === state.columnId);
    if (!liveCol) return;

    const scrollParent = state.scrollParent;
    const savedScrollLeft = scrollParent?.scrollLeft ?? 0;

    const eff = this.effectiveWidth(liveCol);
    const deltaX = event.clientX - state.lastClientX;
    const minW = this.minWidthFor(liveCol);
    const next = Math.max(minW, Math.min(COL_MAX_WIDTH, eff + deltaX));

    this.resizing.set({ ...state, lastClientX: event.clientX });
    this.columnResize.emit({ columnId: state.columnId, width: next });

    if (scrollParent) {
      queueMicrotask(() => {
        scrollParent.scrollLeft = savedScrollLeft;
      });
    }
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  onDocumentPointerUp(event: PointerEvent): void {
    const state = this.resizing();
    if (!state || event.pointerId !== state.pointerId) return;
    this.finishColumnResize();
  }

  private finishColumnResize(): void {
    if (this.resizing()) this.columnResizeEnd.emit();
    this.resizing.set(null);
  }

  @HostListener('document:selectstart', ['$event'])
  onSelectStart(event: Event): boolean {
    if (this.resizing()) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  onColumnDragStart(event: DragEvent, col: ListViewColumn): void {
    if (col.type === 'name') return;
    if (!event.dataTransfer) return;
    event.dataTransfer.setData('text/plain', col.id);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/json', JSON.stringify({ columnId: col.id }));
  }

  onColumnDragOver(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
  }

  onColumnDrop(event: DragEvent, targetCol: ListViewColumn): void {
    event.preventDefault();
    const raw = event.dataTransfer?.getData('application/json') || event.dataTransfer?.getData('text/plain');
    const draggedId = raw?.startsWith('{') ? (JSON.parse(raw) as { columnId: string }).columnId : raw;
    if (!draggedId || draggedId === 'name') return;
    const targetId = targetCol.id;
    const ids = this.columns().map((c) => c.id);
    const fromIdx = ids.indexOf(draggedId);
    if (fromIdx === -1) return;
    const toIdx = targetId === 'name' ? 1 : ids.indexOf(targetId);
    if (toIdx === -1) return;
    const newIds = ids.slice();
    newIds.splice(fromIdx, 1);
    const insertIdx = fromIdx < toIdx ? toIdx - 1 : toIdx;
    newIds.splice(insertIdx, 0, draggedId);
    this.columnReorder.emit(newIds);
  }

  canMoveColumnLeft(col: ListViewColumn): boolean {
    if (col.type === 'name') return false;
    const ids = this.columns().map((c) => c.id);
    const index = ids.indexOf(col.id);
    return index > 1;
  }

  canMoveColumnRight(col: ListViewColumn): boolean {
    if (col.type === 'name') return false;
    const ids = this.columns().map((c) => c.id);
    const index = ids.indexOf(col.id);
    return index !== -1 && index < ids.length - 1;
  }

  moveColumn(col: ListViewColumn, direction: -1 | 1, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (col.type === 'name') return;
    const ids = this.columns().map((c) => c.id);
    const from = ids.indexOf(col.id);
    if (from === -1) return;
    const target = from + direction;
    if (target < 1 || target >= ids.length) return;
    const nextIds = ids.slice();
    const [moved] = nextIds.splice(from, 1);
    nextIds.splice(target, 0, moved);
    this.columnReorder.emit(nextIds);
  }

  /**
   * Tooltip limitado: o `title` nativo com texto enorme cobre o ecrã e piora layout/hover.
   * O texto visível na célula fica sempre truncado à largura da coluna (uma linha).
   */
  assigneeResolvedUserId(item: GroupedListItem): string | null {
    const id = (item.assigneeUserId ?? '').trim();
    if (id) return id;
    return this.companyUsers.resolveUserIdByDisplayName(item.assigneeName);
  }

  hasAssigneeDisplay(item: GroupedListItem): boolean {
    return !!(item.assigneeName ?? '').trim() || !!this.assigneeResolvedUserId(item);
  }

  personFieldUserId(item: GroupedListItem, col: ListViewColumn): string | null {
    if (col.type !== 'custom' || !col.customFieldKey || !item.customFields) return null;
    const raw = item.customFields[col.customFieldKey];
    if (raw == null) return null;
    const s = String(raw).trim();
    if (!s || s === '—') return null;
    if (isGuidString(s)) return s;
    return this.companyUsers.resolveUserIdByDisplayName(s) ?? this.companyUsers.resolveUserIdByDisplayName(this.cellValue(item, col));
  }

  personFieldLabel(item: GroupedListItem, col: ListViewColumn): string {
    const v = this.cellValue(item, col);
    return v === '—' ? '' : v;
  }

  descriptionListTooltip(raw: string | null | undefined): string | null {
    const t = (raw ?? '').trim();
    if (!t.length) return null;
    const max = 220;
    return t.length <= max ? t : `${t.slice(0, max)}…`;
  }

  cellValue(item: GroupedListItem, col: ListViewColumn): string {
    switch (col.type) {
      case 'name':
        return item.title;
      case 'code':
        return item.code?.trim() || '—';
      case 'latestComment':
        return item.latestComment?.trim() || '—';
      case 'description':
        return item.description?.trim() || '—';
      case 'status':
        return item.status;
      case 'assignee':
        return item.assigneeName;
      case 'priority':
        return item.priority;
      case 'dueDate':
        return item.dueDate ?? '—';
      case 'custom': {
        const val = col.customFieldKey && item.customFields ? item.customFields[col.customFieldKey] : null;
        if (val == null || val === '') return this.customEditorFor(col)?.emptyLabel ?? '—';
        if (typeof val === 'string' && looksLikeFileFieldJsonString(val)) {
          return formatFileFieldValueForList(val, 'Arquivo anexado');
        }
        const editor = col.customFieldKey ? this.customFieldEditors()[col.customFieldKey] : undefined;
        if (editor?.kind === 'date') {
          const raw = String(val).trim();
          if (!raw) return '—';
          let forFmt = raw;
          if (raw.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(raw) && !raw.includes('T')) {
            forFmt = `${raw.slice(0, 10)}T12:00:00.000Z`;
          } else {
            const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);
            if (m) {
              const d = m[1].padStart(2, '0');
              const mo = m[2].padStart(2, '0');
              forFmt = `${m[3]}-${mo}-${d}T12:00:00.000Z`;
            }
          }
          return this.formatDateFn()(forFmt);
        }
        if (editor?.kind === 'confirmation-date') return formatConfirmationDate(val);
        if (editor?.kind === 'item-list') return formatItemListSummary(val);
        if (editor?.kind === 'datetime') {
          const formatted = formatCustomDateTimeForDisplay(String(val));
          return formatted || '—';
        }
        if (editor?.kind === 'time') {
          const formatted = formatCustomTimeForDisplay(String(val));
          return formatted || '—';
        }
        if (editor?.kind === 'select' && editor.selectOptions?.length) {
          const opt = editor.selectOptions.find((o) => o.value === val);
          if (opt) return opt.label;
        }
        if (editor?.kind === 'phone' && typeof val === 'string') {
          const trimmed = val.trim();
          if (!trimmed.length) return '—';
          const formatted = formatStoredBrazilPhoneForDisplay(trimmed);
          return formatted || trimmed;
        }
        if (editor?.kind === 'number') {
          const currency = editor.formatCurrency === true;
          const rawStr = String(val).trim();
          const schemaDp = clampNumberFieldDecimalPlaces(editor.numberDecimalPlaces);
          if (!currency && shouldPreserveLeadingZeroIntegerRaw(rawStr, schemaDp)) {
            return rawStr.replace(/\s+/g, '');
          }
          const n = (currency ? parseMoneyStringPtBr(rawStr) : null) ?? parseFlexibleNumberString(rawStr);
          if (n == null || !Number.isFinite(n)) return '—';
          const dp = currency ? 2 : effectiveListNumberDecimalPlaces(schemaDp, rawStr, n);
          const rounded = roundToDecimalPlaces(n, dp);
          if (currency) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(rounded);
          }
          return formatNumberFieldForDisplayPtBr(rounded, dp, {
            percentSuffix: editor.numberShowPercentSuffix === true,
          });
        }
        return val;
      }
      default:
        return '—';
    }
  }

  availableStatuses(): Array<{ id: string; label: string }> {
    return this.statuses().length ? this.statuses() : this.groups().map((group) => ({ id: group.status, label: group.label }));
  }

  statusSelectOptions(): SelectItem[] {
    return this.availableStatuses().map((s) => ({ value: s.id, label: s.label }));
  }

  assigneeSelectOptions(): SelectItem[] {
    const names = this.assignees();
    const items = names.map((n) => {
      const uid = this.companyUsers.resolveUserIdByDisplayName(n);
      return { value: n, label: n, ...(uid ? { userId: uid } : {}) } satisfies SelectItem;
    });
    if (!items.some((o) => o.value === '')) {
      return [{ value: '', label: '—', userId: null }, ...items];
    }
    return items;
  }

  /** Opções do combobox de responsável: grupos da página + nomes extra (ex.: legado) com `userId` quando possível. */
  assigneeComboboxFlatOptions(): SelectItem[] {
    const gr = this.assigneeOptionGroups();
    const fromNames = this.assigneeSelectOptions();
    if (!gr.length) return fromNames;

    const seen = new Set<string>();
    const out: SelectItem[] = [];
    for (const g of gr) {
      for (const o of g.options) {
        if (seen.has(o.value)) continue;
        seen.add(o.value);
        out.push(o);
      }
    }
    for (const o of fromNames) {
      if (seen.has(o.value)) continue;
      seen.add(o.value);
      const uid =
        (o.userId ?? '').trim() ||
        (o.value ? this.companyUsers.resolveUserIdByDisplayName(o.value) : null);
      out.push(uid ? { ...o, userId: uid } : o);
    }
    return out;
  }

  prioritySelectOptionsFor(item: GroupedListItem): SelectItem[] {
    return this.priorityOptionsFor(item).map((p) => ({ value: p, label: p }));
  }

  dueDatePickerValue(item: GroupedListItem): string | null {
    const iso = item.dueDate?.slice(0, 10) ?? '';
    return iso.length >= 10 ? iso : null;
  }

  onStatusComboboxChange(item: GroupedListItem, value: string | null): void {
    const nextStatus = (value ?? '').trim();
    if (!nextStatus || nextStatus === item.status) {
      this.finishEditingCell();
      return;
    }
    this.itemStatusChange.emit({ itemId: item.id, status: nextStatus });
    this.finishEditingCell();
  }

  onTitleBlur(item: GroupedListItem, event: Event): void {
    const el = event.target as HTMLInputElement | null;
    if (!el) return;
    const next = el.value.trim();
    if (!next) {
      el.value = item.title;
      this.finishEditingCell();
      return;
    }
    if (next === item.title) {
      this.finishEditingCell();
      return;
    }
    this.itemTitleCommit.emit({ itemId: item.id, title: next });
    this.finishEditingCell();
  }

  onPriorityComboboxChange(item: GroupedListItem, value: string | null): void {
    const next = this.canonicalPriority((value ?? '').trim()) as ItemPriority;
    const current = this.canonicalPriority(String(item.priority ?? '')) as ItemPriority;
    if (!next || next === current) {
      this.finishEditingCell();
      return;
    }
    this.itemPriorityChange.emit({ itemId: item.id, priority: next });
    this.finishEditingCell();
  }

  priorityOptionsFor(item: GroupedListItem): string[] {
    const fromList = this.priorities()
      .map((p) => this.canonicalPriority(String(p)))
      .filter((p) => p.length > 0);
    const uniq = Array.from(new Set(fromList));
    const cur = this.canonicalPriority(String(item.priority ?? ''));
    if (cur && !uniq.includes(cur)) uniq.unshift(cur);
    return uniq;
  }

  /** Valor do <select> que sempre casa com alguma <option> (evita browser cair na primeira opção). */
  prioritySelectModel(item: GroupedListItem): string {
    const opts = this.priorityOptionsFor(item);
    const cur = this.canonicalPriority(String(item.priority ?? ''));
    if (cur && opts.includes(cur)) return cur;
    return opts[0] ?? cur;
  }

  onAssigneeComboboxChange(item: GroupedListItem, value: string | null): void {
    const next = (value ?? '').trim();
    if (next === (item.assigneeName ?? '').trim()) {
      this.finishEditingCell();
      return;
    }
    this.itemAssigneeChange.emit({ itemId: item.id, assigneeName: next });
    this.finishEditingCell();
  }

  onDueDatePickerChange(item: GroupedListItem, value: string | null): void {
    const raw = (value ?? '').trim();
    const next: string | null = raw.length ? raw : null;
    const prevIso = item.dueDate?.slice(0, 10) ?? '';
    const prev = prevIso.length >= 10 ? prevIso : '';
    if ((next ?? '') === prev) {
      this.finishEditingCell();
      return;
    }
    this.itemDueDateCommit.emit({ itemId: item.id, dueDate: next });
    this.finishEditingCell();
  }

  onDueDateClear(item: GroupedListItem, event: Event): void {
    this.preventRowNavigation(event);
    const prevIso = item.dueDate?.slice(0, 10) ?? '';
    const hasDate = prevIso.length >= 10;
    if (!hasDate) {
      this.finishEditingCell();
      return;
    }
    this.itemDueDateCommit.emit({ itemId: item.id, dueDate: null });
    this.finishEditingCell();
  }

  customFieldDatePickerValue(item: GroupedListItem, col: ListViewColumn): string | null {
    if (col.type !== 'custom' || !col.customFieldKey) return null;
    const raw = String(item.customFields?.[col.customFieldKey] ?? '').trim();
    if (!raw) return null;
    if (raw.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);
    if (m) {
      const d = m[1].padStart(2, '0');
      const mo = m[2].padStart(2, '0');
      return `${m[3]}-${mo}-${d}`;
    }
    return null;
  }

  onCustomFieldDatePickerChange(item: GroupedListItem, col: ListViewColumn, value: string | null): void {
    const next = (value ?? '').trim() || null;
    const prev = this.customFieldDatePickerValue(item, col);
    if ((next ?? '') === (prev ?? '')) {
      this.finishEditingCell();
      return;
    }
    if (!col.customFieldKey) return;
    this.itemCustomFieldCommit.emit({
      itemId: item.id,
      fieldKey: col.customFieldKey,
      value: next,
    });
    this.finishEditingCell();
  }

  onCustomFieldDateClear(item: GroupedListItem, col: ListViewColumn, event: Event): void {
    this.preventRowNavigation(event);
    if (!col.customFieldKey) return;
    const prev = this.customFieldDatePickerValue(item, col);
    if (!prev) {
      this.finishEditingCell();
      return;
    }
    this.itemCustomFieldCommit.emit({
      itemId: item.id,
      fieldKey: col.customFieldKey,
      value: null,
    });
    this.finishEditingCell();
  }

  customEditorFor(col: ListViewColumn): GroupedListCustomFieldEditor | null {
    if (col.type !== 'custom' || !col.customFieldKey) return null;
    return this.customFieldEditors()[col.customFieldKey] ?? null;
  }

  /** Coluna de campo custom tipo select (dropdown na lista). */
  isCustomSelectColumn(col: ListViewColumn): boolean {
    return this.customEditorFor(col)?.kind === 'select';
  }

  /** `truncate` no wrapper da célula aplica overflow:hidden e recorta avatares. */
  cellAllowsTruncate(col: ListViewColumn): boolean {
    if (col.type === 'assignee') return false;
    if (col.type === 'custom' && this.customEditorFor(col)?.displayAsPerson) return false;
    return true;
  }

  ratingLevelsFor(editor: GroupedListCustomFieldEditor | null): number {
    const raw = editor?.ratingLevels ?? 5;
    const n = Number(raw);
    if (!Number.isFinite(n)) return 5;
    return Math.max(2, Math.min(5, Math.round(n)));
  }

  ratingFieldViewModel(col: ListViewColumn, editor: GroupedListCustomFieldEditor): CustomFieldViewModel {
    const key = col.customFieldKey ?? col.id;
    return {
      fieldId: key,
      key,
      name: col.label,
      type: 'RATING',
      required: false,
      readOnly: false,
      showInQuickView: true,
      showInApp: false,
      options: [],
      order: 0,
      placeholder: null,
      formulaDefinition: null,
      formulaResultDecimalPlaces: 0,
      numberDecimalPlaces: 0,
      numberShowPercentSuffix: false,
      ratingLevels: this.ratingLevelsFor(editor),
      codePrefix: '',
      codeFormat: 'NUMERIC',
      codeLength: 4,
      codeSeparator: 'NONE',
      projectReusableFieldId: null,
      fromProjectReusable: false,
      personRestrictToWorkflowMembers: false,
      collectViaWhatsApp: false,
      whatsAppPrompt: null,
      whatsAppCollectionOrder: null,
    };
  }

  itemListFieldViewModel(col: ListViewColumn, editor: GroupedListCustomFieldEditor): CustomFieldViewModel {
    return { ...createEmptyCustomField(0), fieldId: col.customFieldKey ?? col.id,
      name: col.label, type: 'ITEM_LIST', readOnly: editor.readOnly ?? false, required: editor.required ?? false };
  }

  onItemListChanged(item: GroupedListItem, col: ListViewColumn, value: CustomFieldRawValue): void {
    if (value === null || typeof value === 'string') this.onCustomFieldValueCommit(item, col, value);
  }

  ratingRawValue(item: GroupedListItem, col: ListViewColumn): CustomFieldRawValue {
    if (col.type !== 'custom' || !col.customFieldKey) return null;
    const v = item.customFields?.[col.customFieldKey];
    if (v == null || v === '') return null;
    return v;
  }

  onCfRatingChanged(item: GroupedListItem, col: ListViewColumn, value: CustomFieldRawValue): void {
    if (value == null || value === '') {
      this.onCustomFieldValueCommit(item, col, null);
      return;
    }
    if (typeof value === 'number') {
      this.onCustomFieldValueCommit(item, col, value);
      return;
    }
    this.onCustomFieldValueCommit(item, col, String(value));
  }

  onListFileFieldPicked(item: GroupedListItem, col: ListViewColumn, file: File): void {
    if (!col.customFieldKey) return;
    this.itemCustomFieldFileAppend.emit({ itemId: item.id, fieldKey: col.customFieldKey, file });
  }

  fileFieldRawValue(item: GroupedListItem, col: ListViewColumn): string | null {
    if (col.type !== 'custom' || !col.customFieldKey) return null;
    const v = item.customFields?.[col.customFieldKey];
    if (v == null) return null;
    const s = String(v).trim();
    return s.length ? s : null;
  }

  isEditingCell(itemId: string, columnId: string): boolean {
    const active = this.activeEditCell();
    return !!active && active.itemId === itemId && active.columnId === columnId;
  }

  startEditingCell(itemId: string, columnId: string, event?: Event): void {
    if (event) this.preventRowNavigation(event);
    const col = this.columns().find((c) => c.id === columnId) ?? null;
    if (col?.type === 'custom' && col.customFieldKey) {
      const editor = this.customFieldEditors()[col.customFieldKey];
      if (editor?.readOnly === true) return;
    }
    this.activeEditCell.set({ itemId, columnId });
  }

  finishEditingCell(): void {
    this.activeEditCell.set(null);
  }

  onCustomFieldCommit(item: GroupedListItem, col: ListViewColumn, event: Event): void {
    this.preventRowNavigation(event);
    if (col.type !== 'custom' || !col.customFieldKey) return;
    const target = event.target as HTMLInputElement | HTMLSelectElement | null;
    let next = (target?.value ?? '').trim();
    const editor = this.customFieldEditors()[col.customFieldKey];
    if (editor?.readOnly === true) {
      this.finishEditingCell();
      return;
    }
    if (editor?.kind === 'number') {
      const currency = editor.formatCurrency === true;
      const parseNum = (s: string) =>
        currency ? parseMoneyStringPtBr(s) : parseFlexibleNumberString(s);
      const currentRaw = String(item.customFields?.[col.customFieldKey] ?? '').trim();
      const schemaDp = clampNumberFieldDecimalPlaces(editor.numberDecimalPlaces);
      const currentPreserved = !currency && shouldPreserveLeadingZeroIntegerRaw(currentRaw, schemaDp);
      const curNum = currentRaw.length ? parseNum(currentRaw) : null;
      const dp = currency ? 2 : effectiveListNumberDecimalPlaces(schemaDp, currentRaw, curNum);
      const curRounded =
        curNum == null || !Number.isFinite(curNum) ? null : roundToDecimalPlaces(curNum, dp);
      const curApi =
        curRounded == null ? null : formatNumberFieldValueForApi(curRounded, dp);

      if (!next.length) {
        if (curApi == null) {
          this.finishEditingCell();
          return;
        }
        this.itemCustomFieldCommit.emit({
          itemId: item.id,
          fieldKey: col.customFieldKey,
          value: null,
        });
        this.finishEditingCell();
        return;
      }

      if (!currency && shouldPreserveLeadingZeroIntegerRaw(next, schemaDp)) {
        const nextRaw = next.replace(/\s+/g, '');
        const currentRawNormalized = currentRaw.replace(/\s+/g, '');
        if (currentPreserved && nextRaw === currentRawNormalized) {
          this.finishEditingCell();
          return;
        }
        this.itemCustomFieldCommit.emit({
          itemId: item.id,
          fieldKey: col.customFieldKey,
          value: nextRaw,
        });
        this.finishEditingCell();
        return;
      }

      const parsed = parseNum(next);
      if (parsed == null || !Number.isFinite(parsed)) {
        this.finishEditingCell();
        return;
      }
      const commitDp = currency ? 2 : effectiveListNumberDecimalPlaces(schemaDp, next, parsed);
      const rounded = roundToDecimalPlaces(parsed, commitDp);
      const nextApi = formatNumberFieldValueForApi(rounded, commitDp);
      if (nextApi === curApi) {
        this.finishEditingCell();
        return;
      }
      this.itemCustomFieldCommit.emit({
        itemId: item.id,
        fieldKey: col.customFieldKey,
        value: nextApi,
      });
      this.finishEditingCell();
      return;
    }
    if (editor?.kind === 'phone' && next.length) {
      const normalized = normalizeBrazilPhoneForSubmit(next);
      if (/^\+[1-9]\d{6,14}$/.test(normalized)) {
        next = normalized;
      }
    }
    const current = (item.customFields?.[col.customFieldKey] ?? '').trim();
    if (next === current) return;
    this.itemCustomFieldCommit.emit({
      itemId: item.id,
      fieldKey: col.customFieldKey,
      value: next.length ? next : null,
    });
    this.finishEditingCell();
  }

  onCustomFieldValueCommit(
    item: GroupedListItem,
    col: ListViewColumn,
    value: string | number | boolean | null
  ): void {
    if (col.type !== 'custom' || !col.customFieldKey) return;
    const editor = this.customFieldEditors()[col.customFieldKey];
    if (editor?.readOnly === true) {
      this.finishEditingCell();
      return;
    }
    if (editor?.kind === 'rating') {
      const max = this.ratingLevelsFor(editor);
      const currentRaw = String(item.customFields?.[col.customFieldKey] ?? '').trim();
      const cur = parseRatingQuickViewDisplay(currentRaw, max);
      let nextNum: number | null =
        value == null || (typeof value === 'string' && !value.trim().length)
          ? null
          : typeof value === 'number'
            ? value
            : Number(String(value).replace(',', '.').trim());
      if (nextNum != null && !Number.isFinite(nextNum)) nextNum = null;
      const nextNorm =
        nextNum == null ? null : parseRatingQuickViewDisplay(String(nextNum), max);
      if (cur === nextNorm || (cur == null && nextNorm == null)) {
        this.finishEditingCell();
        return;
      }
      this.itemCustomFieldCommit.emit({
        itemId: item.id,
        fieldKey: col.customFieldKey,
        value: nextNorm,
      });
      this.finishEditingCell();
      return;
    }

    if (editor?.kind === 'boolean') {
      const currentRaw = item.customFields?.[col.customFieldKey];
      const current = this.parseBooleanRawValue(currentRaw);
      const next =
        typeof value === 'boolean' ? value : this.parseBooleanRawValue(value == null ? null : String(value));
      if (next == null || current === next) {
        this.finishEditingCell();
        return;
      }
      this.itemCustomFieldCommit.emit({
        itemId: item.id,
        fieldKey: col.customFieldKey,
        value: next,
      });
      this.finishEditingCell();
      return;
    }

    let next = value == null ? '' : String(value).trim();
    if (editor?.kind === 'phone' && next.length) {
      const normalized = normalizeBrazilPhoneForSubmit(next);
      if (/^\+[1-9]\d{6,14}$/.test(normalized)) {
        next = normalized;
      }
    }
    const current = String(item.customFields?.[col.customFieldKey] ?? '').trim();
    if (next === current) {
      this.finishEditingCell();
      return;
    }
    this.itemCustomFieldCommit.emit({
      itemId: item.id,
      fieldKey: col.customFieldKey,
      value: value == null || next.length === 0 ? null : next,
    });
    this.finishEditingCell();
  }

  isCustomBooleanChecked(item: GroupedListItem, col: ListViewColumn): boolean {
    if (col.type !== 'custom' || !col.customFieldKey) return false;
    return this.parseBooleanRawValue(item.customFields?.[col.customFieldKey]) === true;
  }

  booleanCellText(item: GroupedListItem, col: ListViewColumn): string {
    if (col.type !== 'custom' || !col.customFieldKey) return 'Não';
    const parsed = this.parseBooleanRawValue(item.customFields?.[col.customFieldKey]);
    if (parsed == null) return 'Não';
    return parsed ? 'Sim' : 'Não';
  }

  onCustomFieldBooleanToggle(item: GroupedListItem, col: ListViewColumn, event: Event): void {
    this.preventRowNavigation(event);
    const next = !this.isCustomBooleanChecked(item, col);
    this.onCustomFieldValueCommit(item, col, next);
  }

  /** Alinha rótulos da API/lista com as opções do select (`Baixa` / `Média` / `Alta`). */
  canonicalPriority(raw: string): string {
    const s = (raw ?? '').normalize('NFC').trim().toLowerCase();
    if (!s) return '';
    if (s === 'baixa' || s === 'low' || s === 'baixo') return 'Baixa';
    if (s === 'alta' || s === 'high' || s === 'urgente') return 'Alta';
    if (
      s === 'média' ||
      s === 'media' ||
      s === 'medium' ||
      s === 'mã©dia' ||
      s === 'normal'
    ) {
      return 'Média';
    }
    return (raw ?? '').normalize('NFC').trim();
  }

  preventRowNavigation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  stopRowNavigation(event: Event): void {
    event.stopPropagation();
  }

  openItemDetails(item: GroupedListItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.navigateToItemDetail()) {
      this.itemDetailNavigate.emit(item);
      return;
    }
    void this.router.navigate(this.itemUrlFn()(item));
  }

  onNameClick(itemId: string, columnId: string, event: Event): void {
    this.preventRowNavigation(event);
    if (this.pendingNameClickTimer) {
      clearTimeout(this.pendingNameClickTimer);
      this.pendingNameClickTimer = null;
    }
    this.pendingNameClickTimer = setTimeout(() => {
      this.startEditingCell(itemId, columnId);
      this.pendingNameClickTimer = null;
    }, 220);
  }

  onNameDoubleClick(item: GroupedListItem, event: Event): void {
    if (this.pendingNameClickTimer) {
      clearTimeout(this.pendingNameClickTimer);
      this.pendingNameClickTimer = null;
    }
    this.openItemDetails(item, event);
  }

  onAdvanceStage(item: GroupedListItem, event: Event): void {
    this.preventRowNavigation(event);
    const statuses = this.availableStatuses().map((status) => status.id);
    const currentIndex = statuses.indexOf(item.status);
    if (currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % statuses.length;
    const nextStatus = statuses[nextIndex];
    if (!nextStatus || nextStatus === item.status) return;
    this.itemStatusChange.emit({ itemId: item.id, status: nextStatus });
  }

  canMoveUp(item: GroupedListItem): boolean {
    const statuses = this.availableStatuses().map((status) => status.id);
    const currentIndex = statuses.indexOf(item.status);
    return currentIndex > 0;
  }

  canMoveDown(item: GroupedListItem): boolean {
    const statuses = this.availableStatuses().map((status) => status.id);
    const currentIndex = statuses.indexOf(item.status);
    return currentIndex !== -1 && currentIndex < statuses.length - 1;
  }

  onMoveUp(item: GroupedListItem, event: Event): void {
    this.preventRowNavigation(event);
    const statuses = this.availableStatuses().map((status) => status.id);
    const currentIndex = statuses.indexOf(item.status);
    if (currentIndex <= 0) return;
    const previousStatus = statuses[currentIndex - 1];
    if (!previousStatus || previousStatus === item.status) return;
    this.itemStatusChange.emit({ itemId: item.id, status: previousStatus });
  }

  onMoveDown(item: GroupedListItem, event: Event): void {
    this.preventRowNavigation(event);
    const statuses = this.availableStatuses().map((status) => status.id);
    const currentIndex = statuses.indexOf(item.status);
    if (currentIndex === -1 || currentIndex >= statuses.length - 1) return;
    const nextStatus = statuses[currentIndex + 1];
    if (!nextStatus || nextStatus === item.status) return;
    this.itemStatusChange.emit({ itemId: item.id, status: nextStatus });
  }

  customCellColor(item: GroupedListItem, col: ListViewColumn): string | null {
    if (col.type !== 'custom' || !col.customFieldKey) return null;
    const rawValue = item.customFields?.[col.customFieldKey];
    if (!rawValue) return null;
    const mapByField = this.customFieldOptionColors()[col.customFieldKey] ?? {};
    const color = mapByField[rawValue];
    return typeof color === 'string' && color.trim() ? color : null;
  }

  customCellTextColor(hexColor: string | null): string {
    if (!hexColor) return 'var(--muted)';
    const normalized = hexColor.trim().toUpperCase();
    const match = /^#([0-9A-F]{6})$/.exec(normalized);
    if (!match) return 'var(--muted)';
    const rgb = match[1];
    const r = Number.parseInt(rgb.slice(0, 2), 16);
    const g = Number.parseInt(rgb.slice(2, 4), 16);
    const b = Number.parseInt(rgb.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.62 ? '#111827' : '#FFFFFF';
  }

  /** Fundo suave para badges de select (cor da opção vem do backend). */
  stageBadgeBackground(color: string): string {
    return rgbaFromHex(color, 0.14) ?? `${color}24`;
  }

  stageBadgeBorder(color: string): string {
    return rgbaFromHex(color, 0.22) ?? `${color}33`;
  }

  stageBadgeTextColor(color: string): string {
    if (!color) return 'var(--text)';
    const normalized = color.trim().toUpperCase();
    const match = /^#([0-9A-F]{6})$/.exec(normalized);
    if (!match) return 'var(--text)';
    const rgb = match[1];
    const r = Number.parseInt(rgb.slice(0, 2), 16);
    const g = Number.parseInt(rgb.slice(2, 4), 16);
    const b = Number.parseInt(rgb.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    // Fundo translúcido (~14% opacidade) → texto deve ser escuro na maioria;
    // só usar branco quando a cor base for muito escura (fundo fica quase preto)
    if (luminance < 0.18) return '#FFFFFF';
    // Para cores claras/médias: usar uma versão escurecida da própria cor
    const darken = (c: number) => Math.round(c * 0.45);
    const dr = darken(r);
    const dg = darken(g);
    const db = darken(b);
    return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
  }

  priorityVisualKind(item: GroupedListItem): 'low' | 'medium' | 'high' | 'default' {
    const c = this.canonicalPriority(String(item.priority ?? ''));
    if (c === 'Baixa') return 'low';
    if (c === 'Média' || c === 'MÃ©dia') return 'medium';
    if (c === 'Alta') return 'high';
    return 'default';
  }

  dueDateVisualKind(item: GroupedListItem): 'overdue' | 'soon' | 'normal' {
    const iso = item.dueDate?.slice(0, 10) ?? '';
    if (iso.length < 10) return 'normal';
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return 'normal';
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000);
    if (diffDays < 0) return 'overdue';
    if (diffDays <= 2) return 'soon';
    return 'normal';
  }

  quickFieldTextColor(hexColor: string | null): string {
    return this.customCellTextColor(hexColor);
  }

  /** Valor exibido no <input> de texto ao editar célula (telefone mascarado BR quando aplicável). */
  customFieldPlainInputDisplay(item: GroupedListItem, col: ListViewColumn): string {
    if (col.type !== 'custom' || !col.customFieldKey) return '';
    const raw = item.customFields?.[col.customFieldKey];
    const s = String(raw ?? '').trim();
    if (!s) return '';
    const ed = this.customEditorFor(col);
    if (ed?.kind === 'phone') return formatStoredBrazilPhoneForDisplay(s);
    if (ed?.kind === 'date') {
      const iso = this.customFieldDatePickerValue(item, col);
      return iso ?? '';
    }
    if (ed?.kind === 'number') {
      const currency = ed.formatCurrency === true;
      const schemaDp = clampNumberFieldDecimalPlaces(ed.numberDecimalPlaces);
      if (!currency && shouldPreserveLeadingZeroIntegerRaw(s, schemaDp)) {
        return s.replace(/\s+/g, '');
      }
      const n = (currency ? parseMoneyStringPtBr(s) : null) ?? parseFlexibleNumberString(s);
      if (n == null || !Number.isFinite(n)) return '';
      const dp = currency ? 2 : effectiveListNumberDecimalPlaces(schemaDp, s, n);
      const rounded = roundToDecimalPlaces(n, dp);
      if (currency) {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(rounded);
      }
      return formatNumberFieldForDisplayPtBr(rounded, dp, {
        percentSuffix: ed.numberShowPercentSuffix === true,
      });
    }
    return s;
  }

  customSelectOptions(item: GroupedListItem, col: ListViewColumn, editor: GroupedListCustomFieldEditor): SelectItem[] {
    const fromEditor = (editor.selectOptions ?? []).filter((o) => o.value.trim().length > 0);
    const fallback = (editor.options ?? [])
      .map((value) => {
        const t = (value ?? '').trim();
        return t ? ({ value: t, label: t } as SelectItem) : null;
      })
      .filter((x): x is SelectItem => !!x);
    const base = fromEditor.length ? fromEditor : fallback;
    const seen = new Set<string>();
    const deduped: SelectItem[] = [];
    for (const opt of base) {
      if (seen.has(opt.value)) continue;
      seen.add(opt.value);
      deduped.push(opt);
    }
    const current = col.customFieldKey ? (item.customFields?.[col.customFieldKey] ?? '').trim() : '';
    if (current && !seen.has(current)) deduped.unshift({ value: current, label: current });
    if (editor.emptyLabel) deduped.unshift({ value: '', label: editor.emptyLabel });
    return deduped;
  }

  isCopyableCell(col: ListViewColumn): boolean {
    if (col.type === 'code') return true;
    if (col.type !== 'custom') return false;
    const editor = this.customEditorFor(col);
    return editor?.copyable === true;
  }

  cellCopyText(item: GroupedListItem, col: ListViewColumn): string {
    if (col.type === 'code') return item.code?.trim() || '';
    const val = this.cellValue(item, col);
    return val === '—' ? '' : val;
  }

  private parseBooleanRawValue(value: unknown): boolean | null {
    if (value === true || value === false) return value;
    if (typeof value === 'number') return value === 1 ? true : value === 0 ? false : null;
    if (typeof value !== 'string') return null;
    const normalized = value.trim().toLowerCase();
    if (!normalized.length) return null;
    if (normalized === 'true' || normalized === '1' || normalized === 'sim') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'nao' || normalized === 'não')
      return false;
    return null;
  }
}
